import { AddToCartSuccessCartUrl } from '@components/additionalProducts/AddToCartSuccessCartUrl';
import { AddToCartSuccessImage } from '@components/additionalProducts/AddToCartSuccessImage';
import { ADDITIONAL_PRODUCT } from '@constants';
import {
    OutboundLinkContext,
    OutboundLinkDestinationCategory,
    OutboundLinkShape,
    TrackEvent,
} from '@enums';
import {
    AdditionalProductFragment,
    VariantFragment,
} from '@graphql/generated/graphql';
import { useAnalytics } from '@lib/analytics/analyticsContext';
import {
    InternalAddToCartPayload,
    InternalAddToCartResponsePayload,
    InternalMessageType,
    isInternaldMessageEvent,
    sendMessageToEmbed,
} from '@lib/internal-messages';
import { useTranslation } from 'next-i18next';
import { useCallback, useRef, useState, useEffect } from 'react';
import { toast } from 'sonner';

interface Props {
    context: OutboundLinkContext;
    additionalProduct: AdditionalProductFragment;
    variant: VariantFragment | null;
}

const ADD_TO_CART_TIMEOUT = 1500;

export const useAddToCart = ({
    context,
    additionalProduct,
    variant,
}: Props) => {
    const { t } = useTranslation(ADDITIONAL_PRODUCT, {
        keyPrefix: 'actions.add_to_cart',
    });
    const { track } = useAnalytics();
    const [isAddingToCart, setIsAddingToCart] = useState<boolean>(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const buildInternalAddToCartPayload = useCallback(
        (variant: VariantFragment): InternalAddToCartPayload => ({
            product: {
                id: additionalProduct.id,
                source: additionalProduct.Source,
            },
            variant: {
                id: variant.id,
                gtin: variant.Gtin,
                source: variant.Source,
            },
        }),
        [additionalProduct]
    );

    const trackAddToCart = useCallback(
        (internalAddToCartPayload: InternalAddToCartPayload): void => {
            track(TrackEvent.OutboundLink, {
                category: 'click',
                destination: additionalProduct.Url || '',
                destination_name: additionalProduct.Name,
                destination_category:
                    OutboundLinkDestinationCategory.AdditionalProduct,
                context: OutboundLinkContext.StoryPage,
                shape: OutboundLinkShape.Button,
                action: InternalMessageType.AddToCart,
                actionPayload: internalAddToCartPayload,
            });
        },
        [additionalProduct, track]
    );

    const handleAddToCartResponse = useCallback(
        async (payload: InternalAddToCartResponsePayload): Promise<void> => {
            if (payload.success) {
                toast.success(t('success'), {
                    action: variant ? (
                        <AddToCartSuccessImage variant={variant} />
                    ) : null,
                    description: payload.cartUrl ? (
                        <AddToCartSuccessCartUrl
                            context={context}
                            cartUrl={payload.cartUrl}
                        />
                    ) : null,
                });
            } else {
                toast.error(payload.error || t('error'));
            }

            setIsAddingToCart(false);
        },
        [t, context, variant]
    );

    const clearTimeoutIfNeeded = useCallback((): void => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    const messageHandler = useCallback(
        (messageEvent: MessageEvent): void => {
            if (
                isInternaldMessageEvent(messageEvent) &&
                messageEvent.data.type === InternalMessageType.AddToCartResponse
            ) {
                clearTimeoutIfNeeded();
                window.removeEventListener('message', messageHandler);
                handleAddToCartResponse(messageEvent.data.payload);
            }
        },
        [clearTimeoutIfNeeded, handleAddToCartResponse]
    );

    const startAddToCartTimeout = useCallback((): void => {
        clearTimeoutIfNeeded();
        timeoutRef.current = setTimeout(() => {
            window.removeEventListener('message', messageHandler);
            setIsAddingToCart(false);
            toast.success(t('success'), {
                action: variant ? (
                    <AddToCartSuccessImage variant={variant} />
                ) : null,
            });
        }, ADD_TO_CART_TIMEOUT);
    }, [t, variant, clearTimeoutIfNeeded, messageHandler]);

    const addToCart = useCallback((): void => {
        if (!variant) return;
        setIsAddingToCart(true);
        const internalAddToCartPayload = buildInternalAddToCartPayload(variant);
        sendMessageToEmbed({
            type: InternalMessageType.AddToCart,
            payload: internalAddToCartPayload,
        });
        window.addEventListener('message', messageHandler);
        startAddToCartTimeout();
        trackAddToCart(internalAddToCartPayload);
    }, [
        variant,
        buildInternalAddToCartPayload,
        messageHandler,
        trackAddToCart,
        startAddToCartTimeout,
    ]);

    useEffect(() => {
        return () => {
            clearTimeoutIfNeeded();
            window.removeEventListener('message', messageHandler);
        };
    }, [clearTimeoutIfNeeded, messageHandler]);

    return { isAddingToCart, addToCart };
};
